import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Dropdown, Input, Progress, Select, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  MoreHorizontal,
  Pencil,
  Play,
  RefreshCw,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import type { CalendarItem } from '@/types/contentPlanner';
import { isVisibleInSelectedMonth } from './contentTrackerFilters';
import { useMultiWorkspaceCalendar, contentPlannerKeys } from '@/hooks/useContentPlannerAPI';
import { useTenantStore } from '@/stores/useTenantStore';
import { apiClient } from '@/services/apiClient';
import { useQueryClient } from '@tanstack/react-query';
import {
  StatusPill,
  KeywordChip,
  deriveStatusVariant,
  isProcessing,
  isPublished,
  RowActionButtons,
  type RowPrimaryAction,
} from '@/components/content-planner/shared';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { universalBlogEditorPath } from '@/utils/editorRoutes';
import { byGenerationRecency } from '../sortItems';

const { Text } = Typography;

type GroupBucket = 'active' | 'done';

/** Active = anything still in flight (in progress, ready, scheduled). Done = published or failed. */
function deriveGroup(item: CalendarItem): GroupBucket {
  if (isPublished(item) || item.status === 'failed') return 'done';
  return 'active';
}

const GROUPS: Array<{ id: GroupBucket; label: string; hint?: string }> = [
  { id: 'active', label: 'Active', hint: 'in progress, ready & scheduled' },
  { id: 'done', label: 'Done', hint: 'published & failed' },
];

type SortKey = 'title' | 'status' | 'keyword' | 'schedule' | 'words' | 'created';
type SortDir = 'asc' | 'desc';
type SortState = { key: SortKey; dir: SortDir };

const DEFAULT_SORT: SortState = { key: 'created', dir: 'desc' };

/** Columns that read most-recent / largest-first on their first click. */
const DESC_FIRST_KEYS: ReadonlySet<SortKey> = new Set(['schedule', 'words', 'created']);

function parseDate(value?: string | null): number | null {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : t;
}

function tenantTiebreak(a: CalendarItem, b: CalendarItem): number {
  return (a._wsTenant || '').localeCompare(b._wsTenant || '');
}

/** Build a comparator for the current sort. Missing values always sink to the bottom. */
function makeComparator(sort: SortState) {
  const mult = sort.dir === 'asc' ? 1 : -1;
  const valueOf = (item: CalendarItem): string | number | null => {
    switch (sort.key) {
      case 'title':
        return (item.title || item.keyword || '').toLowerCase();
      case 'status':
        return deriveStatusVariant(item);
      case 'keyword':
        return (item.keyword || '').toLowerCase();
      case 'schedule':
        return parseDate(item.scheduled_date || item.scheduled_publish_time);
      case 'words':
        return item.word_count ?? null;
      case 'created':
      default:
        return parseDate(item.created_at);
    }
  };
  return (a: CalendarItem, b: CalendarItem): number => {
    const av = valueOf(a);
    const bv = valueOf(b);
    if (av === null && bv === null) return tenantTiebreak(a, b);
    if (av === null) return 1; // a sinks regardless of direction
    if (bv === null) return -1; // b sinks regardless of direction
    let c: number;
    if (typeof av === 'number' && typeof bv === 'number') c = av - bv;
    else c = String(av).localeCompare(String(bv));
    if (c !== 0) return c * mult;
    return tenantTiebreak(a, b);
  };
}

function SortableTh({
  label,
  sortKey,
  sort,
  onSort,
  align,
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onSort: (key: SortKey) => void;
  align?: 'right';
}) {
  const active = sort.key === sortKey;
  const caret = active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕';
  return (
    <th
      className={cn(
        'ctt-th ctt-th-sortable',
        align === 'right' && 'ctt-th-right',
        active && 'ctt-th-active',
      )}
      role="button"
      tabIndex={0}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => onSort(sortKey)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSort(sortKey);
        }
      }}
    >
      <span className="ctt-th-inner">
        {label}
        <span className={cn('ctt-sort-caret', active && 'ctt-sort-caret-active')}>{caret}</span>
      </span>
    </th>
  );
}

function formatStageLabel(stage?: string | null): string {
  if (!stage) return '';
  return stage
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function domainToDisplayName(domain: string): string {
  const base = domain.replace(/\.[^.]+$/, '');
  const words = base
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_.]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function EditableCell({
  contentId,
  field,
  value,
  tenantId,
  onSaved,
  onOpen,
  placeholder,
  multiline,
}: {
  contentId: string;
  field: string;
  value: string | null;
  tenantId: string;
  onSaved: () => void;
  onOpen?: () => void;
  placeholder?: string;
  multiline?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (text === (value || '')) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await apiClient.patch(`/blog-creation/content/${contentId}?tenant_id=${tenantId}`, {
        [field]: text || null,
      });
      onSaved();
      setEditing(false);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    const InputComponent = multiline ? Input.TextArea : Input;
    const extraProps = multiline ? { autoSize: { minRows: 1, maxRows: 3 } } : {};
    return (
      <InputComponent
        autoFocus
        size="small"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={save}
        onPressEnter={(e) => {
          if (!multiline) {
            e.preventDefault();
            save();
          }
        }}
        disabled={saving}
        style={{ fontSize: 12, padding: '2px 6px' }}
        onClick={(e) => e.stopPropagation()}
        {...extraProps}
      />
    );
  }

  return (
    <span
      className={cn(
        'inline-block min-h-[18px] rounded px-1 py-0.5 hover:bg-warm-bg',
        onOpen ? 'cursor-pointer' : 'cursor-text',
      )}
      onClick={(e) => {
        e.stopPropagation();
        if (onOpen) {
          onOpen();
          return;
        }
        setEditing(true);
      }}
    >
      {value || (
        <span style={{ color: 'var(--color-ink-3)', fontStyle: 'italic', opacity: 0.6 }}>
          {placeholder || 'Click to edit...'}
        </span>
      )}
    </span>
  );
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'published', label: 'Published' },
  { value: 'completed', label: 'Completed' },
  { value: 'draft', label: 'Draft' },
  { value: 'generating', label: 'Generating' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
];

interface ContentTrackerTableProps {
  /** External items — when omitted the table fetches via useMultiWorkspaceCalendar */
  items?: CalendarItem[];
  isLoading?: boolean;
  onSchedule?: (item: CalendarItem) => void;
  onViewDetails?: (item: CalendarItem) => void;
  onGenerate?: (item: CalendarItem) => void;
  onCreateNew?: () => void;
  getRowMenuItems?: (item: CalendarItem) => MenuProps['items'];
  /** Primary status actions surfaced as inline buttons (rest stay in ⋯). */
  getRowPrimaryActions?: (item: CalendarItem) => RowPrimaryAction[];
  /** Lifted selection state (chrome-impl owns this in ContentPlannerPage) */
  selectedIds?: Set<string> | string[];
  onToggleSelect?: (id: string) => void;
  /** Newly-created content id to scroll to + highlight (from `?new=<id>` handoff) */
  highlightContentId?: string;
}

const COL_COUNT = 8;

export function ContentTrackerTable({
  items: externalItems,
  isLoading: externalLoading,
  onSchedule,
  onViewDetails,
  onGenerate,
  getRowMenuItems: externalGetRowMenuItems,
  getRowPrimaryActions,
  selectedIds,
  onToggleSelect,
  highlightContentId,
}: ContentTrackerTableProps) {
  const navigate = useNavigate();
  const { currentTenant } = useTenantStore();
  const queryClient = useQueryClient();
  const usesExternalItems = externalItems !== undefined;

  // Scoped to the single selected workspace (no cross-tenant blending).
  const tenantIds = useMemo(
    () => (usesExternalItems || !currentTenant ? [] : [currentTenant]),
    [usesExternalItems, currentTenant],
  );

  const { items: calendarItems, isLoading: calendarLoading } = useMultiWorkspaceCalendar(tenantIds);

  const items = externalItems ?? calendarItems;
  const isLoading = externalLoading ?? (externalItems ? false : calendarLoading);

  const invalidateCalendar = () =>
    queryClient.invalidateQueries({ queryKey: [...contentPlannerKeys.all, 'calendar'] });

  const getTenant = (record: CalendarItem) => record._wsTenant || currentTenant || '';

  const [internalSelected, setInternalSelected] = useState<Set<string>>(new Set());
  const selectedSet = useMemo(() => {
    if (selectedIds === undefined) return internalSelected;
    return selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
  }, [selectedIds, internalSelected]);

  const toggleSelect = (id: string) => {
    if (onToggleSelect) {
      onToggleSelect(id);
      return;
    }
    setInternalSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedMonth, setSelectedMonth] = useState(() => dayjs().startOf('month'));
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  // Whether the user has explicitly clicked a column header. The initial view
  // pins in-flight generations to the top; an explicit sort must not.
  const [userSorted, setUserSorted] = useState(false);

  const toggleSort = (key: SortKey) => {
    setUserSorted(true);
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: DESC_FIRST_KEYS.has(key) ? 'desc' : 'asc' },
    );
  };

  // #6 — scroll the freshly-created row into view + brief teal highlight pulse.
  const highlightRowRef = useRef<HTMLTableRowElement | null>(null);
  const [pulseId, setPulseId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!highlightContentId) return;
    setPulseId(highlightContentId);
    // Defer scroll until the row has rendered.
    const scrollTimer = window.setTimeout(() => {
      highlightRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    // Fade the pulse after ~2.5s.
    const pulseTimer = window.setTimeout(() => setPulseId(undefined), 2500);
    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(pulseTimer);
    };
  }, [highlightContentId]);

  const filtered = useMemo(() => {
    let result = items;

    if (!usesExternalItems) {
      // Scheduled items are scoped to the selected month; unscheduled backlog
      // items (drafts / pending / in-progress) always show so a freshly-created
      // item never disappears when the table is on a different month.
      result = result.filter((i) => isVisibleInSelectedMonth(i, selectedMonth));

      if (statusFilter !== 'all') {
        result = result.filter((i) => i.status === statusFilter);
      }
    }

    return [...result];
  }, [items, statusFilter, selectedMonth, usesExternalItems]);

  const grouped = useMemo(() => {
    const byGroup: Record<GroupBucket, CalendarItem[]> = { active: [], done: [] };
    for (const item of filtered) {
      byGroup[deriveGroup(item)].push(item);
    }
    // Until the user sorts, float newly-started generations (and most-recent
    // items) to the top of the Active group so in-flight work is always visible.
    // Once the user picks an explicit column sort, honor that instead.
    //
    // This keys off `userSorted` rather than comparing against DEFAULT_SORT:
    // "Created" is a desc-first column, so clicking it produces exactly
    // DEFAULT_SORT ({created, desc}). Matching on the value alone therefore
    // re-applied the processing-first pin and the column appeared not to sort
    // descending at all (ascending worked, since it never matched).
    const cmp = makeComparator(sort);
    byGroup.active.sort(userSorted ? cmp : byGenerationRecency);
    byGroup.done.sort(cmp);
    return byGroup;
  }, [filtered, sort, userSorted]);

  const monthTabs = useMemo(() => {
    const months = [];
    for (let i = -3; i <= 3; i++) {
      months.push(dayjs().startOf('month').add(i, 'month'));
    }
    return months;
  }, []);

  /** The Created column: the row's created_at date, nothing else. */
  const renderCreated = (item: CalendarItem) => {
    if (!item.created_at) return null;
    return (
      <span style={{ color: 'var(--color-ink-3)', fontSize: 11 }}>
        {dayjs(item.created_at).format('MMM D')}
      </span>
    );
  };


  const getDefaultRowMenuItems = (item: CalendarItem): MenuProps['items'] => [
    {
      key: 'details',
      label: 'View Details',
      icon: <FileText size={14} />,
      onClick: () => (onViewDetails ? onViewDetails(item) : navigate(universalBlogEditorPath(item.id))),
    },
    {
      key: 'editor',
      label: 'Edit Content',
      icon: <Pencil size={14} />,
      onClick: () => navigate(universalBlogEditorPath(item.id)),
    },
    ...(onSchedule
      ? [
          {
            key: 'schedule',
            label: 'Schedule / Reschedule',
            icon: <CalendarDays size={14} />,
            onClick: () => onSchedule(item),
          },
        ]
      : []),
    ...(item.wp_url
      ? [
          {
            key: 'live',
            label: 'Open Live Post',
            icon: <ExternalLink size={14} />,
            onClick: () => window.open(item.wp_url!, '_blank', 'noopener,noreferrer'),
          },
        ]
      : []),
  ];

  const getRowMenuItems = externalGetRowMenuItems ?? getDefaultRowMenuItems;

  return (
    <div className="content-tracker-table">
      {!usesExternalItems && (
        <div className="mb-4 flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => setSelectedMonth((m) => m.subtract(1, 'month'))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-1 overflow-x-auto">
            {monthTabs.map((m) => (
              <Button
                key={m.format('YYYY-MM')}
                variant="ghost"
                size="sm"
                onClick={() => setSelectedMonth(m)}
                className={cn(
                  'h-8 whitespace-nowrap rounded-md px-3 text-xs font-medium transition-colors',
                  selectedMonth.isSame(m, 'month')
                    ? 'bg-coral text-white hover:bg-coral-hover hover:text-white'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {m.format('MMM YYYY')}
              </Button>
            ))}
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => setSelectedMonth((m) => m.add(1, 'month'))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {!usesExternalItems && (
        <Space style={{ marginBottom: 12 }} wrap>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            options={STATUS_OPTIONS}
            style={{ width: 160 }}
            size="small"
          />
          <Text style={{ color: 'var(--color-ink-3)', fontSize: 12 }}>
            {filtered.length} articles in {selectedMonth.format('MMMM YYYY')}
          </Text>
        </Space>
      )}

      <div className="ctt-scroll">
        <table className="ctt-table">
          <thead>
            <tr>
              <th className="ctt-th ctt-th-checkbox" />
              <SortableTh label="Title" sortKey="title" sort={sort} onSort={toggleSort} />
              <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
              <SortableTh label="Primary keyword" sortKey="keyword" sort={sort} onSort={toggleSort} />
              <SortableTh label="Schedule" sortKey="schedule" sort={sort} onSort={toggleSort} />
              <SortableTh label="Words" sortKey="words" sort={sort} onSort={toggleSort} align="right" />
              <SortableTh label="Created" sortKey="created" sort={sort} onSort={toggleSort} align="right" />
              <th className="ctt-th ctt-th-checkbox" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={COL_COUNT} className="ctt-empty">
                  Loading...
                </td>
              </tr>
            )}

            {!isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={COL_COUNT} className="ctt-empty">
                  No content yet. Create your first article in the Calendar view.
                </td>
              </tr>
            )}

            {!isLoading &&
              GROUPS.map((group) => {
                const groupItems = grouped[group.id];
                if (groupItems.length === 0) return null;
                return (
                  <React.Fragment key={group.id}>
                    <tr className="ctt-group-row">
                      <td colSpan={COL_COUNT} className="ctt-group-cell">
                        <span className="ctt-group-label">{group.label}</span>
                        <span className="ctt-group-count">{groupItems.length}</span>
                        {group.hint && <span className="ctt-group-hint">{group.hint}</span>}
                      </td>
                    </tr>
                    {groupItems.map((item) => {
                      const variant = deriveStatusVariant(item);
                      const isSelected = selectedSet.has(item.id);
                      const isIdea = item.status === 'pending';
                      const tenant = item._wsTenant;
                      const tenantTitle = tenant ? domainToDisplayName(tenant) : undefined;
                      const isHighlight = highlightContentId === item.id;
                      const isPulsing = pulseId === item.id;

                      return (
                        <tr
                          key={item.id}
                          ref={isHighlight ? highlightRowRef : undefined}
                          className={cn(
                            'ctt-row group',
                            isSelected && 'ctt-row-selected',
                            isPulsing && 'ctt-row-pulse',
                          )}
                          onClick={() => onViewDetails?.(item)}
                          style={{
                            cursor: onViewDetails ? 'pointer' : undefined,
                            // relative so the hover action menu resolves to the
                            // row and can open next to the cursor via
                            // --cp-reveal-left (set in onMouseEnter). Table
                            // parts are abspos containing blocks in all
                            // evergreen browsers.
                            position: 'relative',
                          }}
                          onMouseEnter={(e) => {
                            // Anchor the action menu's LEFT edge just right of the
                            // cursor's x so it opens beside where the mouse first
                            // entered the row (+12px keeps the pointer off the
                            // first option). Set once on enter — it must not
                            // chase the cursor.
                            const rect = e.currentTarget.getBoundingClientRect();
                            e.currentTarget.style.setProperty(
                              '--cp-reveal-left',
                              `${Math.max(8, Math.round(e.clientX - rect.left) + 12)}px`,
                            );
                          }}
                        >
                          <td
                            className="ctt-td ctt-td-checkbox"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(item.id)}
                              style={{ accentColor: 'var(--color-coral)' }}
                              aria-label="Select row"
                            />
                          </td>
                          <td className="ctt-td ctt-td-title">
                            <span
                              className="serif"
                              style={{
                                fontSize: 13.5,
                                fontWeight: 600,
                                color: 'var(--color-ink)',
                                letterSpacing: '-0.005em',
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 340,
                              }}
                            >
                              <EditableCell
                                contentId={item.id}
                                field="title"
                                value={item.title}
                                tenantId={getTenant(item)}
                                onSaved={invalidateCalendar}
                                onOpen={() => onViewDetails?.(item)}
                                placeholder={item.keyword || 'Add title...'}
                              />
                            </span>
                          </td>
                          <td className="ctt-td">
                            {isProcessing(item) ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 130 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <RefreshCw
                                    size={12}
                                    className="text-teal animate-spin"
                                    style={{ flexShrink: 0 }}
                                  />
                                  {item.progress_percentage != null && (
                                    <span style={{ color: 'var(--color-teal-dark)', fontSize: 12, fontWeight: 600 }}>
                                      {Math.round(item.progress_percentage)}%
                                    </span>
                                  )}
                                  <StatusPill status={variant} />
                                </div>
                                {item.progress_percentage != null && (
                                  <Progress
                                    percent={Math.round(item.progress_percentage)}
                                    size="small"
                                    showInfo={false}
                                    strokeColor="var(--color-teal)"
                                  />
                                )}
                                {item.current_stage && (
                                  <span
                                    style={{ color: 'var(--color-ink-3)', fontSize: 11 }}
                                    title={formatStageLabel(item.current_stage)}
                                  >
                                    {formatStageLabel(item.current_stage)}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <StatusPill status={variant} />
                            )}
                          </td>
                          <td className="ctt-td">
                            {item.keyword ? (
                              <KeywordChip variant="primary" title={item.keyword}>
                                <span
                                  style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 160,
                                    display: 'inline-block',
                                  }}
                                >
                                  {item.keyword}
                                </span>
                              </KeywordChip>
                            ) : (
                              <span style={{ color: 'var(--color-ink-3)', fontSize: 11 }}>—</span>
                            )}
                          </td>
                          <td className="ctt-td">
                            {item.scheduled_date ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSchedule?.(item);
                                }}
                                disabled={!onSchedule}
                                title={onSchedule ? 'Reschedule' : undefined}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  background: 'transparent',
                                  border: 'none',
                                  padding: 0,
                                  color: variant === 'Scheduled' ? 'var(--color-coral-dark)' : 'var(--color-ink-2)',
                                  fontWeight: 600,
                                  fontSize: 11.5,
                                  cursor: onSchedule ? 'pointer' : 'default',
                                }}
                              >
                                <CalendarDays size={12} />
                                {dayjs(item.scheduled_date).format('MMM D, YYYY')}
                              </button>
                            ) : onSchedule ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSchedule(item);
                                }}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  background: 'transparent',
                                  border: '1px dashed var(--color-warm-border)',
                                  borderRadius: 4,
                                  padding: '2px 8px',
                                  color: 'var(--color-ink-3)',
                                  fontSize: 11,
                                  cursor: 'pointer',
                                }}
                                title="Schedule this article"
                              >
                                <CalendarDays size={11} />
                                Schedule
                              </button>
                            ) : (
                              <span style={{ color: 'var(--color-ink-3)' }}>—</span>
                            )}
                          </td>
                          <td className="ctt-td ctt-td-right">
                            {item.word_count != null ? (
                              <span
                                className="mono"
                                style={{ color: 'var(--color-ink-2)', fontSize: 12 }}
                              >
                                {item.word_count.toLocaleString()}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--color-ink-3)' }}>—</span>
                            )}
                          </td>
                          <td className="ctt-td ctt-td-right">{renderCreated(item)}</td>
                          <td className="ctt-td ctt-td-actions" onClick={(e) => e.stopPropagation()}>
                            {onGenerate && !isProcessing(item) && !isPublished(item) && (item.title || item.keyword) && (
                              <Button
                                size="sm"
                                aria-label="Generate"
                                className="mr-1.5 h-6 gap-1 px-2 text-[11px]"
                                onClick={() => onGenerate(item)}
                              >
                                <Play size={13} />
                                Generate
                              </Button>
                            )}
                            {getRowPrimaryActions ? (
                              <RowActionButtons
                                actions={getRowPrimaryActions(item)}
                                menuItems={getRowMenuItems(item)}
                                revealOnHover
                                anchorToCursor
                              />
                            ) : (
                              <Dropdown
                                menu={{ items: getRowMenuItems(item) }}
                                trigger={['click']}
                                placement="bottomRight"
                              >
                                <button
                                  type="button"
                                  aria-label="Row actions"
                                  style={{
                                    display: 'inline-grid',
                                    placeItems: 'center',
                                    width: 24,
                                    height: 24,
                                    borderRadius: 4,
                                    background: 'transparent',
                                    border: 'none',
                                    color: 'var(--color-ink-3)',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <MoreHorizontal size={16} />
                                </button>
                              </Dropdown>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>

      <style>{`
        .content-tracker-table .ctt-scroll {
          overflow-x: auto;
          background: var(--color-paper);
          border: 1px solid var(--color-warm-border);
          border-radius: 8px;
        }
        .content-tracker-table .ctt-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12.5px;
          font-family: 'Inter', system-ui, sans-serif;
        }
        .content-tracker-table .ctt-th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: var(--color-warm-bg);
          padding: 10px 14px;
          font-size: 10px;
          font-weight: 700;
          color: var(--color-ink-3);
          text-transform: uppercase;
          letter-spacing: 0.08em;
          text-align: left;
          white-space: nowrap;
          border-bottom: 1px solid var(--color-warm-border);
        }
        .content-tracker-table .ctt-th-center { text-align: center; }
        .content-tracker-table .ctt-th-right { text-align: right; }
        .content-tracker-table .ctt-th-checkbox { width: 36px; }
        .content-tracker-table .ctt-th-sortable {
          cursor: pointer;
          user-select: none;
          transition: color 0.12s, background 0.12s;
        }
        .content-tracker-table .ctt-th-sortable:hover {
          color: var(--color-ink);
          background: var(--color-paper-2);
        }
        .content-tracker-table .ctt-th-active { color: var(--color-ink); }
        .content-tracker-table .ctt-th-inner {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .content-tracker-table .ctt-sort-caret {
          font-size: 9px;
          opacity: 0.3;
          transition: opacity 0.12s, color 0.12s;
        }
        .content-tracker-table .ctt-sort-caret-active {
          opacity: 1;
          color: var(--color-coral);
        }
        .content-tracker-table .ctt-row {
          transition: background 0.12s;
        }
        /* Hover emphasis. --color-warm-bg became pure white in the Core
           palette rebrand, which made the old hover invisible — hovering a
           blog row now tints it with the pale accent and draws a left accent
           bar (inset shadow: no layout shift). */
        .content-tracker-table .ctt-row:hover {
          background: color-mix(in srgb, var(--color-coral-light) 55%, #fff);
          box-shadow: inset 3px 0 0 var(--color-coral);
        }
        .content-tracker-table .ctt-row-selected {
          background: var(--color-coral-light);
        }
        .content-tracker-table .ctt-row-selected:hover {
          background: color-mix(in srgb, var(--color-coral-light) 80%, var(--color-coral));
          box-shadow: inset 3px 0 0 var(--color-coral);
        }
        .content-tracker-table .ctt-row-pulse {
          animation: ctt-pulse 2.5s ease-out;
        }
        @keyframes ctt-pulse {
          0% {
            background: var(--color-teal-light);
            box-shadow: inset 3px 0 0 0 var(--color-teal);
          }
          70% {
            background: var(--color-teal-light);
            box-shadow: inset 3px 0 0 0 var(--color-teal);
          }
          100% {
            background: transparent;
            box-shadow: inset 3px 0 0 0 transparent;
          }
        }
        .content-tracker-table .ctt-td {
          padding: 8px 14px;
          border-bottom: 1px solid var(--color-warm-border);
          vertical-align: middle;
        }
        .content-tracker-table .ctt-td-center { text-align: center; }
        .content-tracker-table .ctt-td-right { text-align: right; }
        .content-tracker-table .ctt-td-checkbox { width: 36px; text-align: center; }
        .content-tracker-table .ctt-td-actions { text-align: right; white-space: nowrap; }
        .content-tracker-table .ctt-td-title { max-width: 340px; }
        .content-tracker-table .ctt-group-row {
          background: var(--color-warm-bg);
        }
        .content-tracker-table .ctt-group-cell {
          padding: 8px 14px;
          border-top: 1px solid var(--color-warm-border);
          border-bottom: 1px solid var(--color-warm-border);
        }
        .content-tracker-table .ctt-group-label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--color-ink);
          margin-right: 10px;
        }
        .content-tracker-table .ctt-group-count {
          padding: 1px 7px;
          border-radius: 9px;
          background: var(--color-paper-2);
          color: var(--color-ink-2);
          font-size: 10.5px;
          font-weight: 700;
          margin-right: 10px;
        }
        .content-tracker-table .ctt-group-hint {
          font-size: 11px;
          color: var(--color-ink-3);
          font-weight: 500;
        }
        .content-tracker-table .ctt-empty {
          padding: 32px 16px;
          text-align: center;
          color: var(--color-ink-3);
          font-size: 12.5px;
        }
      `}</style>
    </div>
  );
}
